import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, RendererFactory2, Renderer2 } from '@angular/core';
import { get } from 'lodash';
import { DmDataService } from '../dm-data/dm-data.service';
import { TokenSessionService } from '../session/token-session.service';
import { take } from 'rxjs/operators';

export const THEME_TYPE = {
  LIGHT: 'x_theme-light-genius', // Dark theme uses "no explicit class" (body stays default)
};

@Injectable()
export class ThemeService {
  private renderer: Renderer2;

  public constructor(
    private rendererFactory: RendererFactory2,
    private dmDataService: DmDataService,
    private tokenSessionService: TokenSessionService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.renderer = this.rendererFactory.createRenderer(document, null);
  }
  /**
   * Fetch theme info from backend and normalize to a CSS class name.
   * Always returns one of THEME_TYPE.* values; defaults to DARK on error.
   */
  /**
   * Resolve theme CSS class from backend result.
   * Backend may return 'light' or 'dark'. 'dark' (or any unknown / error) => no class applied.
   * Returns a CSS class string or '' when dark/default.
   */
  public async getThemeByTerminalID(terminalId: string): Promise<string> {
    if (terminalId == null) {
      return '';
    }
    try {
      const res: any = await this.dmDataService
        .fetchThemeByAppCode(terminalId)
        .toPromise();

      const raw = (get(res, 'items[0].terminal_scheme.color_theme', '') || '')
        .toString()
        .trim()
        .toLowerCase();

      // light -> apply light theme class;
      // dark / ''  -> '' (dark default, no class)
      return raw === 'light' ? THEME_TYPE.LIGHT : '';
    } catch {
      // Error => default dark (no class)
      return '';
    }
  }

  /**
   * Initialize theme by querying API; call early (e.g. via APP_INITIALIZER).
   * Falls back gracefully to DARK if API fails.
   */
  public async initTheme(): Promise<void> {
    const terminalId = await this.tokenSessionService.terminalId$
      .pipe(take(1))
      .toPromise();
    const themeClass = await this.getThemeByTerminalID(terminalId);
    this.applyTheme(themeClass);
  }

  /** Apply a theme CSS class to body (idempotent). */
  public applyTheme(themeClass: string): void {
    const body = this.document?.body;
    if (!body) {
      return;
    }
    // Apply new theme only if non-empty (dark = no explicit class)
    if (themeClass) this.renderer.addClass(body, themeClass);
  }

  /**
   * Get the active theme id using a mapper (class-to-theme),
   */
  public getThemeType(): string {
    const body = this.document?.body;
    if (!body) {
      return 'default';
    }

    // Explicit mapper for known/legacy classes
    const classToTheme: Record<string, string> = {
      'x_theme-light-genius': 'light-genius',
    };

    for (const cls of Array.from(body.classList)) {
      const mapped = classToTheme[cls];
      if (mapped) {
        return mapped;
      }
    }

    // Default when no theme class found
    return 'default';
  }
}
